// 1. The `Tile` component is a React functional component designed to display a product's details,
//    including its image, name, price, and color variants.
// 2. It uses state to manage the selected and hovered product variants and provides a color selection feature.
// 3. The product image changes on hover, and users can click on swatches to select different color variants.
// 4. The component also includes a price display and a "Compare" checkbox.
// 5. It leverages the `useMemo` hook to filter unique color variants efficiently from the product's variant list.

import React, { FC, useEffect, useMemo, useState } from 'react';
import { Variant } from 'shared/types/product';
import Image from 'frontastic/lib/image';
import { CenterImageCropSwatch } from 'components/commercetools-ui/organisms/youMayAlsoLike/tail';
import Prices from '../../../product-tile/prices';
import Link from 'components/commercetools-ui/atoms/link';
import { Product, VariantsAlgolia } from './Hit';
import useLocale from 'helpers/hooks/useLocale';
import Price from './price';
import PriceMap from '../AlgoliaPrice/priceMap';
import { useCookies } from 'react-cookie';

interface ProducTileProps {
  product: Product;
  key: number;
  tileKey: number;
  compareProducts: Product[];
  setOpen: (a: boolean) => void;
  badgeData: any;
  handleProductClickAfterSearch: (locale: string, objectId: string[], position: number[]) => void;
  handleProductSwatchClickAfterSearch: (locale: string, objectId: string[], index: number) => void;
}

interface ProductName {
  [locale: string]: string;
}

const Tile: FC<ProducTileProps> = ({
  product,
  key,
  tileKey,
  compareProducts,
  setOpen,
  badgeData,
  handleProductClickAfterSearch,
  handleProductSwatchClickAfterSearch,
}) => {
  const [imageHovered, setImageHovered] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(() => product?.variants?.[0]);
  const [hoveredVariant, setHoveredVariant] = useState<VariantsAlgolia | null>(null);
  const [selectedColour, setSelectedColour] = useState<string | null>(
    product?.variants?.[0]?.attributes?.colour || null,
  );
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const isProductInCompare = compareProducts.flat().some((p) => p.key === product.key);
  const { locale } = useLocale();
  const handleColourSelect = (variant: VariantsAlgolia, objectId: string, index: number) => {
    handleProductSwatchClickAfterSearch(locale, [objectId], index);
    setSelectedColour(variant?.attributes?.colour);
    setSelectedVariant(variant);
  };
  const [price, setPrice] = useState<any>(null);
  const [mrrpPrice, setMrrpPrice] = useState<any>(null);
  const [wasPrice, setWasPrice] = useState<any>(null);

  const uniqueColorVariants = useMemo(() => {
    const seenColours = new Set();
    return product?.variants?.filter((variant: VariantsAlgolia) => {
      const colour = variant?.attributes?.colour;
      if (!colour || seenColours?.has(colour)) {
        return false;
      }
      seenColours?.add(colour);
      return true;
    });
  }, [product.variants]);

  const convertProductNameToUrl = (input: string) => {
    const urlformat = `${input?.toLowerCase().replace(/\s+/g, '-')}`;
    const url = urlformat?.endsWith('-') ? urlformat : `${urlformat}-`;
    return url?.replace(/,/g, '');
  };

  const badgeStyles = {
    'Black November': 'bg-black text-white h-[22px] w-[95px] md:h-[30px] md:w-[160px]',
    Sale: 'bg-[#E30616] text-white h-[22px] w-[28px] md:h-[30px] md:w-[67px]',
    New: 'bg-[#137E57] text-white h-[22px] w-[38px] md:h-[30px] md:w-[68px]',
    'Mega Deals': 'bg-gradient-to-r from-[#272C59] to-[#BD2F48] text-white h-[22px] w-[65px] md:h-[30px] md:w-[112px]',
    Christmas: 'bg-[#137E57] text-white h-[22px] w-[65px] md:h-[30px] md:w-[120px]',
    'Top Gift': 'bg-[#D9BE65] text-black h-[20px] w-[53px] md:h-[30px] md:w-[93px]',
    'Bank Holiday': 'bg-[#BD2F48] text-white h-[22px] w-[79px] md:h-[30px] md:w-[134px]',
    'Bank Holiday Deal': 'bg-[#BD2F48] text-white h-[22px] w-[103px] md:h-[30px] md:w-[173px]',
    'Free Accessory Pack': 'bg-[#1E1E1E] text-white h-[22px] w-[115px] md:h-[30px] md:w-[193px]',
  };
  const discountPercentage = useMemo(() => {
    if (mrrpPrice <= 0 || price <= 0 || mrrpPrice === price) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrpPrice - price) / mrrpPrice) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [mrrpPrice, price]);

  useEffect(() => {
    if (!product?.standalonePrice || product?.standalonePrice.length === 0) {
      // Reset the states if no standalonePrice is available
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
      return;
    }

    const filteredPrice = product?.standalonePrice?.find((price: any) => price?.variantSku === selectedVariant?.sku);
    if (filteredPrice) {
      const isGBP = cookies?.locale_selection?.currency === 'GBP';

      // Set the states based on the selected currency
      setPrice(isGBP ? filteredPrice?.priceGB : filteredPrice?.priceEU);
      setMrrpPrice(isGBP ? filteredPrice?.mrrpPriceGB : filteredPrice?.mrrpPriceEU);
      setWasPrice(isGBP ? filteredPrice?.wasPriceGB : filteredPrice?.wasPriceEU);
    } else {
      // Reset the states if no price is found
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
    }
  }, [product?.standalonePrice, selectedVariant?.sku, cookies?.locale_selection?.currency]);

  return (
    <div className="flex h-full flex-col justify-between border px-10 pt-30 md:border-0">
      <div
        className="relative"
        onClick={() => handleProductClickAfterSearch(locale, [product?.objectID], [tileKey + 1])}
      >
        <Link
          link={`/${product?.categories?.[locale?.replace('_', '-')]?.lvl0?.join('/')}/${
            convertProductNameToUrl((product?.name as ProductName)?.[locale?.replace('_', '-')]) +
            (product?.slug as any)?.[locale?.replace('_', '-')] +
            '.html'
          }`}
        >
          <div
            onMouseEnter={() => setImageHovered(true)}
            onMouseLeave={() => setImageHovered(false)}
            className="relative max-w-[330px]"
          >
            <Image
              src={hoveredVariant?.images?.[0] || selectedVariant?.images?.[0]}
              alt={product?.name?.['en-GB'] ?? ''}
              style={{ objectFit: 'contain', objectPosition: 'center' }}
              className="w-[100%]  rounded-sm group-hover:opacity-75 lg:min-h-[330px]"
            />
            {discountPercentage > 0 && (
              <div className="absolute left-8 top-40 z-10 flex h-[26px] min-w-[51px] items-center justify-center rounded-md border border-[#1E1E1E] p-8">
                <p
                  className="font-body text-14
                 font-medium leading-4 text-black"
                >{`-${discountPercentage}%`}</p>
              </div>
            )}
            <div className="absolute right-0 top-0 flex flex-col items-end gap-y-3">
              {badgeData?.badgeData?.data?.dataSource?.campaign_Items?.map((item: any) =>
                (badgeStyles as any)[item] ? (
                  <div
                    key={item}
                    className={`flex items-center justify-center text-11 md:text-18 ${(badgeStyles as any)[item]}`}
                  >
                    <p className="text-center">{item}</p>
                  </div>
                ) : null,
              )}
            </div>

            {(product?.cartDiscount?.length ?? 0) > 0 && (
              <div className="absolute bottom-0 w-full items-center justify-center bg-[#00d195]">
                <p className="px-5 py-8  text-center font-body text-16 font-medium leading-5">
                  {product?.cartDiscount?.[0]?.description?.['en-GB']}
                </p>
              </div>
            )}
          </div>

          <div className="mt-4 px-40 text-center">
            <div className="relative w-full flex-grow overflow-hidden text-ellipsis pb-[15px] text-[18px] font-medium leading-[22px] md:text-[18px] md:leading-[22px] lg:max-h-[81px]">
              {(product?.name as ProductName)?.['en-GB']}
            </div>
          </div>
        </Link>
      </div>

      <div className="mt-4 text-center md:mt-6">
        <div className="flex flex-nowrap items-center justify-center text-[32px] font-bold leading-[32px] text-[#1e1e1e]">
          {/* <Prices price={selectedVariant?.prices} discountedPrice={selectedVariant?.discountedPrice} /> */}
          <PriceMap price={price} discountedPercentage={discountPercentage} mrrpPrice={mrrpPrice} />
        </div>
      </div>

      {/* <div className="mt-6 flex items-center justify-center py-8">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            onClick={() => {
              storeProductToSession(product), setOpen(true);
            }}
            checked={isProductInCompare}
            className="text-primary form-checkbox h-20 w-20 rounded-sm"
          />

          <span className="text-sm md:text-base">Compare</span>
        </label>
      </div> */}

      <div className="mt-8 flex min-h-[84px] flex-wrap justify-center gap-4">
        {uniqueColorVariants?.map((variant: VariantsAlgolia, index: number) => (
          <div
            key={index}
            className={`relative inline-block h-[24px] w-[24px] cursor-pointer pl-2  ${
              hoveredVariant === variant ? ' ' : ''
            }`}
            onMouseEnter={() => setHoveredVariant(variant)}
            onMouseLeave={() => setHoveredVariant(null)}
            onClick={() => handleColourSelect(variant, product?.objectID, index + 1)}
            title={variant?.attributes?.colour}
          >
            <CenterImageCropSwatch
              imgUrl={variant?.images?.[0]}
              altText="swatch image"
              sku={variant?.sku}
              isActive={selectedColour === variant?.attributes?.colour}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Tile;
